import { useState, useEffect } from "react";
import { api, getMe, getPublicMaintenance, logout, setView, type MeResult, type PublicMaintenance } from "./api";
import { useLocation, navigate } from "./router";
import { useAsync } from "./hooks";
import { Landing } from "./components/Landing";
import { NoOperatorAccess } from "./components/NoOperatorAccess";
import { Overview } from "./components/Overview";
import { Clients } from "./components/Clients";
import { AuditLog } from "./components/AuditLog";
import { ReviewQueue } from "./components/ReviewQueue";
import { Scrapers } from "./components/Scrapers";
import { NewScraper } from "./components/NewScraper";
import { HealingEvents } from "./components/HealingEvents";
import { Runs, type RunFilter } from "./components/Runs";
import { FleetScrapers, type ScraperFilter } from "./components/FleetScrapers";
import { DeadLetters } from "./components/DeadLetters";
import { ApiKeys } from "./components/ApiKeys";
import { ApiDocs } from "./components/ApiDocs";
import { SignupsPaused } from "./components/SignupsPaused";
import { Maintenance } from "./components/Maintenance";
import { RequestAccess } from "./components/RequestAccess";
import { Login } from "./components/Login";
import { Preferences } from "./components/Preferences";
import { Usage } from "./components/Usage";
import { Home } from "./components/Home";
import { GettingStarted } from "./components/GettingStarted";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { Feedback } from "./components/Feedback";
import { UpdateToast } from "./components/UpdateToast";
import { InviteAcceptedToast } from "./components/InviteAcceptedToast";
import { PendingInvites } from "./components/PendingInvites";
import { Settings } from "./components/Settings";
import { HealLadder } from "./components/HealLadder";
import { Users } from "./components/Users";
import { Team } from "./components/Team";
import { OrgSwitcher } from "./components/OrgSwitcher";
import { AccountMenu } from "./components/AccountMenu";
import type { ReviewQueueData } from "./types";

type Tab =
  | "overview"
  | "clients"
  | "runs"
  | "fleetScrapers"
  | "review"
  | "healing"
  | "audit"
  | "dlq"
  | "feedback"
  | "scrapers"
  | "new"
  | "keys"
  | "docs"
  | "settings"
  | "ladder"
  | "users"
  | "team"
  | "prefs"
  | "usage"
  | "home";

/**
 * Two planes, because that is the real domain:
 *   PLATFORM — cross-tenant, operator-only (overview / clients / waiting / heals / settings)
 *   FLEET    — the scrapers themselves (+ keys / api), which a customer also sees
 * A customer gets only the second, and no section headings — three items need no structure.
 */
// operatorOnly: shown only on the platform plane. customerOnly: shown only to a customer (hidden for
// an operator — e.g. the Fleet, which an operator reaches per-client via Clients -> "Scrapers ->").
type NavItem = { id: Tab; label: string; operatorOnly?: boolean; customerOnly?: boolean };

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: "Platform",
    items: [
      { id: "overview", label: "Overview", operatorOnly: true },
      { id: "clients", label: "Clients", operatorOnly: true },
    ],
  },
  {
    // The operational-monitoring cluster — grouped so the rail reads as categories, not a flat list.
    section: "Operations",
    items: [
      { id: "runs", label: "Runs", operatorOnly: true },
      { id: "fleetScrapers", label: "Scrapers", operatorOnly: true },
      { id: "review", label: "Waiting", operatorOnly: true },
      { id: "healing", label: "Heals", operatorOnly: true },
      { id: "dlq", label: "Dead letters", operatorOnly: true },
      { id: "audit", label: "Audit", operatorOnly: true },
      { id: "feedback", label: "Feedback", operatorOnly: true },
    ],
  },
  {
    // The customer's own fleet. Hidden for operators: Home/Scrapers/API are customer-facing, and an
    // operator opens a client's scrapers from Clients (a programmatic tab switch, not this rail item).
    section: "Fleet",
    items: [
      { id: "home", label: "Home", customerOnly: true },
      { id: "scrapers", label: "Scrapers", customerOnly: true },
      { id: "docs", label: "API", customerOnly: true },
    ],
  },
];

/**
 * The bottom-anchored "Settings" category. Config that isn't day-to-day work: the workspace's
 * members + API keys (any member sees these), and the operator-only platform controls. Rendered in
 * its own group pinned to the bottom of the rail (above sign-out). The maintenance/sign-ups screen
 * is labelled "Maintenance" here (its page is titled "Settings") so it doesn't read as Settings ›
 * Settings, and to avoid colliding with the top "Platform" section.
 */
const SETTINGS_ITEMS: NavItem[] = [
  { id: "prefs", label: "Preferences" },
  // Team + Plan & usage are per-workspace (tenant) concerns — a member's roster, a tenant's plan/usage.
  // They don't belong on the cross-tenant operator plane, so they're customerOnly: hidden for an operator
  // on the platform, and back when an operator switches into client-view of its own tenant.
  { id: "team", label: "Team", customerOnly: true },
  { id: "keys", label: "API keys" },
  { id: "usage", label: "Plan & usage", customerOnly: true },
  { id: "settings", label: "Maintenance", operatorOnly: true },
  { id: "ladder", label: "Heal ladder", operatorOnly: true },
  { id: "users", label: "Users", operatorOnly: true },
];

/**
 * Each view's URL. The router maps between tab ⇄ path so every view is a real, linkable URL (back/forward,
 * refresh, deep-link, bookmark). NOTE: the API docs page is `/api-docs`, NOT `/api` — nginx reverse-proxies
 * `/api/*` to the api service, so a client route under `/api` would collide with it.
 */
const TAB_PATH: Record<Tab, string> = {
  overview: "/overview",
  clients: "/clients",
  runs: "/runs",
  fleetScrapers: "/fleet",
  review: "/waiting",
  healing: "/heals",
  dlq: "/dead-letters",
  audit: "/audit",
  feedback: "/feedback",
  scrapers: "/scrapers",
  new: "/scrapers/new",
  keys: "/keys",
  docs: "/api-docs",
  settings: "/maintenance",
  ladder: "/heal-ladder",
  users: "/users",
  team: "/team",
  prefs: "/preferences",
  usage: "/usage",
  home: "/home",
};
const PATH_TAB: Record<string, Tab | undefined> = Object.fromEntries(
  Object.entries(TAB_PATH).map(([t, p]) => [p, t as Tab]),
);

export function App() {
  // undefined = still resolving identity; null = anonymous (show Landing); Me = signed in.
  const [me, setMe] = useState<MeResult | undefined>(undefined);
  const { pathname } = useLocation();
  /** Operator only: Fleet drilled into one client (set from Clients). */
  const [tenantFilter, setTenantFilter] = useState<string | null>(null);
  /** Operator only: the status the Runs view opens on (set when drilling in from Overview). */
  const [runsInitial, setRunsInitial] = useState<RunFilter>("all");
  /** Operator only: the status the fleet Scrapers view opens on (set when drilling in from Overview). */
  const [scrapersInitial, setScrapersInitial] = useState<ScraperFilter>("all");
  /** Signed-in, non-operator user dismissed the /operator elevate screen → let them into the app. */
  const [gateDismissed, setGateDismissed] = useState(false);
  /** Public maintenance flags (anonymous) — drives the landing banner. Undefined = not fetched yet. */
  const [maint, setMaint] = useState<PublicMaintenance | undefined>(undefined);

  useEffect(() => {
    void getMe().then((m) => {
      setMe(m);
      // Land on the right home ONLY when arriving at the root — a deep-link / refresh keeps its own URL.
      // An operator lands on the platform; a customer on their Home dashboard.
      const atRoot = window.location.pathname === "/" || window.location.pathname === "";
      if (m && m !== "maintenance" && atRoot) navigate(m.admin ? "/overview" : "/home", { replace: true });
    });
    // Cheap, cached, never-throws — for the landing banner. Independent of identity.
    void getPublicMaintenance().then(setMaint);
  }, []);

  if (me === undefined) {
    return (
      <div className="gate">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  // Public "request access" form — reachable by anyone (anonymous, blocked, even signed-in), so it sits
  // above the identity gates. The landing banner + the sign-ups-paused screen link here.
  if (location.pathname.replace(/\/+$/, "") === "/request-access") return <RequestAccess />;

  // Full lockdown (tier-2 maintenance): the auth hook 503s this non-operator caller. Show the maintenance
  // page instead of the dashboard/landing. Operators never reach here — they pass the hook (me is their Me).
  if (me === "maintenance") return <Maintenance />;

  // Operator plane is ROLE-BASED and reached only via the /operator deep link (never on the landing
  // page). Gate on `operator` (the IDENTITY) — not `admin` (the current plane) — so an operator who
  // is merely viewing its own tenant as a client isn't wrongly blocked from its own deep link. A
  // signed-in NON-operator gets a plain "no access" notice; signed-out falls through to the landing
  // below (which carries login). The first operator is granted via the CLI (grant-operator).
  const isOperatorGate = location.pathname.replace(/\/+$/, "") === "/operator";
  if (isOperatorGate && me && !me.operator && !gateDismissed) {
    return <NoOperatorAccess me={me} onDismiss={() => setGateDismissed(true)} />;
  }

  // Turned away because sign-ups are paused (OIDC callback redirected to /?signups_closed=1): its
  // OWN screen, not a banner on the landing, so it can't be missed. One button leads back to "/".
  if (!me) {
    if (new URLSearchParams(location.search).has("signups_closed")) return <SignupsPaused />;
    // Dedicated sign-in chooser (the landing's single CTA lands here). nginx SPA-fallback serves /login.
    if (location.pathname.replace(/\/+$/, "") === "/login") return <Login />;
    return <Landing maintenance={maint} />;
  }

  // Signed in, peeking at the marketing landing — a real, F5-safe route (/welcome), not transient state,
  // so a reload stays here instead of snapping back to the previous tab. onEnter drops into the plane's
  // home; the brand mark in the rail links here.
  if (location.pathname.replace(/\/+$/, "") === "/welcome")
    return <Landing authed maintenance={maint} onEnter={() => navigate(me.admin ? "/overview" : "/home")} />;

  // Operator = platform role; customer = Google session. Operator-only tabs are 403 at the API,
  // so we hide them rather than render a screen that will fail.
  const operator = me.admin;

  // The current view is derived from the URL (not React state). `setTab` is a shim so the existing Nav +
  // every child `onNav(tab)` callback drive the router unchanged — navigating instead of setting state.
  // An unknown/root path falls back to the plane's home while the boot effect canonicalizes the URL.
  // /scrapers/:id (a deep-linked scraper panel) resolves to the scrapers tab; /scrapers/new is an exact
  // PATH_TAB hit above, so it wins over this prefix. Unknown/root falls back to the plane's home.
  const tab: Tab =
    PATH_TAB[pathname] ??
    (pathname.startsWith("/scrapers/") ? "scrapers" : operator ? "overview" : "home");
  const setTab = (t: Tab): void => navigate(TAB_PATH[t]);

  async function signOut() {
    setView("operator"); // clear any client-view so the next sign-in starts native
    await logout();
    setMe(null);
    navigate("/", { replace: true });
  }

  // Re-read identity after a profile edit so the change shows everywhere it's used.
  function refreshMe() {
    void getMe().then((m) => {
      if (m) setMe(m);
    });
  }

  // Operator view toggle: an operator flips between the platform plane and viewing its OWN tenant as
  // a client. Persist the choice, refetch identity (the server de-elevates when view=client, so
  // me.admin flips), and land on the right first tab.
  function switchView(v: "client" | "operator") {
    setView(v);
    setTenantFilter(null);
    void getMe().then((m) => {
      if (!m) return;
      setMe(m);
      setTab(v === "client" ? "home" : "overview");
    });
  }

  function openFleetFor(tenantId: string | null) {
    setTenantFilter(tenantId);
    setTab("scrapers");
  }

  return (
    <div className={`app${me.userId ? " has-account" : ""}`}>
      {me.userId && (
        <AccountMenu
          me={me}
          onSignOut={() => void signOut()}
          onUpdated={refreshMe}
          onSwitchView={switchView}
        />
      )}
      <nav className="rail">
        <button
          className="mark mark-btn"
          onClick={() => navigate("/welcome")}
          title="View the home page"
          aria-label="View the home page"
        >
          <b>Amendie</b>
          <span>{operator ? "operator" : "app"}</span>
        </button>

        {/* Multi-org switcher — client-plane only. An operator acts cross-tenant, so their own org
            chip is misleading noise on the platform plane; it returns in client view. */}
        {me.userId && !operator && <OrgSwitcher />}

        <Nav tab={tab} operator={operator} />

        <SettingsNav tab={tab} operator={operator} />

        {/* Foot only renders when it has something: Grafana (operator) and — for admin-KEY auth
            with no account menu — Sign out. A signed-in person signs out from the account menu. */}
        {(operator || !me.userId) && (
          <div className="rail-foot">
            {operator && (
              <a
                className="quiet-btn"
                href={`${location.protocol}//${location.hostname}:3001`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Grafana ↗
              </a>
            )}
            {!me.userId && (
              <button className="quiet-btn" onClick={() => void signOut()}>
                Sign out
              </button>
            )}
          </div>
        )}
      </nav>

      <main className="content">
        {/* Pending workspace invitations — sits above the page until accepted/declined. */}
        {me.userId && <PendingInvites />}
        {tab === "home" && <Home onNav={(t) => setTab(t)} />}
        {tab === "overview" && operator && (
          <Overview
            onNav={(t) => setTab(t)}
            onOpenTenant={openFleetFor}
            onOpenRuns={(s) => {
              setRunsInitial(s ?? "all");
              setTab("runs");
            }}
            onOpenScrapers={(s) => {
              setScrapersInitial(s ?? "all");
              setTab("fleetScrapers");
            }}
          />
        )}
        {tab === "clients" && operator && <Clients onOpenFleet={openFleetFor} />}
        {tab === "runs" && operator && <Runs initialStatus={runsInitial} />}
        {tab === "fleetScrapers" && operator && <FleetScrapers initialStatus={scrapersInitial} />}
        {tab === "review" && operator && <ReviewQueue />}
        {tab === "scrapers" && (
          <Scrapers
            onNew={() => setTab("new")}
            operator={operator}
            tenantFilter={operator ? tenantFilter : null}
            onTenantFilter={setTenantFilter}
          />
        )}
        {tab === "healing" && operator && <HealingEvents />}
        {tab === "audit" && operator && <AuditLog />}
        {tab === "dlq" && operator && <DeadLetters />}
        {tab === "feedback" && operator && <Feedback />}
        {tab === "new" && <NewScraper onCreated={() => setTab("scrapers")} operator={operator} />}
        {tab === "keys" && <ApiKeys />}
        {tab === "team" && <Team me={me} />}
        {tab === "docs" && <ApiDocs />}
        {tab === "settings" && operator && <Settings />}
        {tab === "ladder" && operator && <HealLadder />}
        {tab === "users" && operator && <Users me={me} />}
        {tab === "prefs" && <Preferences me={me} />}
        {tab === "usage" && <Usage />}
      </main>

      {/* Floating onboarding for customers — app-wide (not glued to the scrapers page header), minimizable. */}
      {!operator && me.userId && <GettingStarted onNav={(t) => setTab(t)} />}

      {/* App-wide feedback button — CUSTOMERS only (operators triage it in the Feedback tab + get the
          notification bell, so a floating button would just clutter the operator plane). Sends the
          current page for context. */}
      {!operator && me.userId && <FeedbackWidget />}

      {/* "New version deployed → reload" nudge (everyone) + operator's invite-accepted notice. */}
      <UpdateToast />
      {operator && <InviteAcceptedToast />}
    </div>
  );
}

/**
 * The gold count means one thing only: a fix is waiting on a human — so it counts canaries,
 * not every row on the Waiting page. A paused scraper is a crack, not a repair, and never
 * earns gold.
 */
function Nav({
  tab,
  operator,
}: {
  tab: Tab;
  operator: boolean;
}) {
  const { data } = useAsync<ReviewQueueData | null>(
    () => (operator ? api<ReviewQueueData>("/review/queue") : Promise.resolve(null)),
    [operator],
    operator ? 30_000 : undefined,
  );
  const waiting = data?.canaries.length ?? 0;

  return (
    <div className="nav">
      {NAV.map((group) => {
        const items = group.items.filter(
          (i) => (operator || !i.operatorOnly) && !(operator && i.customerOnly),
        );
        if (items.length === 0) return null;
        return (
          <div className="nav-group" key={group.section}>
            {/* A customer sees three items — headings would be noise. */}
            {operator && <div className="nav-section">{group.section}</div>}
            {items.map((i) => (
              <a
                key={i.id}
                className={tab === i.id ? "tab active" : "tab"}
                aria-current={tab === i.id ? "page" : undefined}
                href={TAB_PATH[i.id]}
                onClick={(e) => {
                  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  navigate(TAB_PATH[i.id]);
                }}
              >
                {i.label}
                {i.id === "review" && waiting > 0 ? <span className="count">{waiting}</span> : null}
              </a>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Bottom-anchored "Settings" category (see {@link SETTINGS_ITEMS}). Unlike the top sections its
 * heading always shows — it's a deliberate, named home for config — and the appearance toggle lives
 * under it. Operator-only items are hidden for customers (they'd 403 at the API anyway).
 */
function SettingsNav({
  tab,
  operator,
}: {
  tab: Tab;
  operator: boolean;
}) {
  const items = SETTINGS_ITEMS.filter(
    (i) => (operator || !i.operatorOnly) && !(operator && i.customerOnly),
  );
  const activeIsSettings = items.some((i) => i.id === tab);
  // Collapsed by default; the "Settings" label is a toggle. Auto-open when the current page IS a
  // settings item so the active tab stays visible. The group is bottom-anchored, so items render ABOVE
  // the toggle — it opens UPWARD. (Mobile hides the toggle + always shows items; see styles.css.)
  const [open, setOpen] = useState(false);
  const showItems = open || activeIsSettings;
  return (
    <div className={`nav-group settings-group${showItems ? "" : " is-collapsed"}`}>
      {items.map((i) => (
        <a
          key={i.id}
          className={tab === i.id ? "tab active" : "tab"}
          aria-current={tab === i.id ? "page" : undefined}
          href={TAB_PATH[i.id]}
          onClick={(e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            navigate(TAB_PATH[i.id]);
          }}
        >
          {i.label}
        </a>
      ))}
      <button
        type="button"
        className="nav-section settings-toggle"
        aria-expanded={showItems}
        onClick={() => setOpen((o) => !o)}
      >
        Settings
        <span className="settings-caret" aria-hidden="true">
          {showItems ? "▾" : "▴"}
        </span>
      </button>
    </div>
  );
}
